/**
 * InstancedFoup.tsx
 *
 * High-performance GPU Instanced rendering for 300mm FOUPs (Front Opening Unified Pods).
 * Replaces plain box proxies with real, high-detail semiconductor wafer transport carriers:
 * - Polycarbonate structural carrier shell with realistic PBR clearcoat
 * - Recessed dark front door with brushed stainless latch
 * - SEMI standard top robotic handling flange
 * - Smoked glass inspection window
 * - Micro cyan cleanroom status LED indicator
 *
 * Decomposes the carrier into constituent sub-mesh parts, rendered via GPU InstancedMesh
 * in exactly 1 draw call per part across hundreds/thousands of stockers and machines.
 */

import React, { useMemo } from 'react';
import { getAmhsKitTemplateParts } from './amhsKitTemplateStore';
import { useAmhsKitTemplateRevision } from './useAmhsKitTemplateRevision';
import * as THREE from 'three';
import {
  instancedAssetManager,
  InstancedMultiMeshGroup,
  type InstanceTransform,
  type SubMeshPart,
} from './InstancedAssetManager';
import {
  getPolycarbonateFoupMaterial,
  getIndustrialDarkChassisMaterial,
  getBrushedAluminumMaterial,
  getPolishedChromeMaterial,
  getCleanroomGlassMaterial,
  getStatusLedMaterial,
} from '../../utils/threeResourcePool';

export interface FoupInstanceSpec {
  id: string;
  position: [number, number, number];
  rotY?: number;
  rotation?: [number, number, number];
  scale?: number;
  color?: string;
  visible?: boolean;
}

export interface InstancedFoupProps {
  foups: readonly FoupInstanceSpec[];
  opacity?: number;
  onSelect?: (id: string) => void;
}

/**
 * Builds the complete, high-fidelity 300mm SEMI Standard FOUP template model.
 */
export function buildFoupTemplateModel(baseColor = '#0F2B5C'): THREE.Group {
  const root = new THREE.Group();
  root.name = 'FoupTemplate';

  // Standardized 300mm FOUP Dimensions: w = 0.40, h = 0.42, d = 0.42
  const w = 0.40;
  const h = 0.42;
  const d = 0.42;
  const bodyY = h * 0.5;

  const bodyMat = getPolycarbonateFoupMaterial(baseColor);
  const doorMat = getIndustrialDarkChassisMaterial();
  const flangeMat = getBrushedAluminumMaterial();
  const latchMat = getPolishedChromeMaterial();
  const windowMat = getCleanroomGlassMaterial('#0A1E2C', 0.85);
  const ledMat = getStatusLedMaterial('#00E5FF', 1.8);

  // 1. Pod Shell Housing (main body)
  const bodyMesh = new THREE.Mesh(
    new THREE.BoxGeometry(w, h * 0.95, d),
    bodyMat
  );
  bodyMesh.position.set(0, bodyY, 0);
  bodyMesh.name = 'FoupBody';
  bodyMesh.castShadow = true;
  bodyMesh.receiveShadow = true;
  root.add(bodyMesh);

  // 2. Front Recessed Door
  const doorMesh = new THREE.Mesh(
    new THREE.BoxGeometry(w * 0.84, h * 0.82, 0.02),
    doorMat
  );
  doorMesh.position.set(0, bodyY, d * 0.505);
  doorMesh.name = 'FoupDoor';
  root.add(doorMesh);

  // 3. Front Smoked Inspection Window
  const windowMesh = new THREE.Mesh(
    new THREE.BoxGeometry(w * 0.62, h * 0.48, 0.01),
    windowMat
  );
  windowMesh.position.set(0, bodyY + h * 0.06, d * 0.518);
  windowMesh.name = 'FoupWindow';
  root.add(windowMesh);

  // 4. SEMI Standard Top Robotic Handling Flange
  const flangeStem = new THREE.Mesh(
    new THREE.BoxGeometry(w * 0.28, 0.035, d * 0.28),
    flangeMat
  );
  flangeStem.position.set(0, h + 0.018, 0);
  flangeStem.name = 'FoupFlangeStem';
  root.add(flangeStem);

  const flangePlate = new THREE.Mesh(
    new THREE.BoxGeometry(w * 0.42, 0.02, d * 0.42),
    flangeMat
  );
  flangePlate.position.set(0, h + 0.045, 0);
  flangePlate.name = 'FoupFlangePlate';
  root.add(flangePlate);

  // 5. Rotary Latch Handle
  const latchMesh = new THREE.Mesh(
    new THREE.CylinderGeometry(0.025, 0.025, 0.012, 16),
    latchMat
  );
  latchMesh.rotation.x = Math.PI / 2;
  latchMesh.position.set(0, bodyY - h * 0.22, d * 0.52);
  latchMesh.name = 'FoupLatch';
  root.add(latchMesh);

  // 6. Cyan Cleanroom Status LED Indicator
  const ledMesh = new THREE.Mesh(
    new THREE.BoxGeometry(0.025, 0.012, 0.01),
    ledMat
  );
  ledMesh.position.set(w * 0.32, bodyY + h * 0.34, d * 0.518);
  ledMesh.name = 'FoupStatusLed';
  root.add(ledMesh);

  return root;
}

function getOrRegisterFoupParts(colorKey = 'default'): SubMeshPart[] {
  if (colorKey === 'default') {
    return getAmhsKitTemplateParts('FoupDefault', () => getOrRegisterFoupPartsFallback(colorKey));
  }
  return getOrRegisterFoupPartsFallback(colorKey);
}

function getOrRegisterFoupPartsFallback(colorKey: string): SubMeshPart[] {
  const assetKey = `foup_${colorKey}`;
  let parts = instancedAssetManager.getAssetParts(assetKey);
  if (!parts) {
    const model = buildFoupTemplateModel(colorKey === 'default' ? '#0F2B5C' : colorKey);
    parts = instancedAssetManager.registerAsset(assetKey, model);
  }
  return parts;
}

export const InstancedFoup: React.FC<InstancedFoupProps> = React.memo(
  ({ foups, onSelect }) => {
    const kitRevision = useAmhsKitTemplateRevision();
    // Group instances by color for accurate batching
    const groups = useMemo(() => {
      const map = new Map<string, InstanceTransform[]>();
      for (let i = 0; i < foups.length; i++) {
        const f = foups[i]!;
        const colorKey = f.color || 'default';
        let list = map.get(colorKey);
        if (!list) {
          list = [];
          map.set(colorKey, list);
        }
        const s = f.scale ?? 1;
        const rot: [number, number, number] = f.rotation
          ? f.rotation
          : [0, f.rotY ?? 0, 0];

        list.push({
          id: f.id || `foup_${i}`,
          position: f.position,
          rotation: rot,
          scale: [s, s, s],
          visible: f.visible !== false,
        });
      }
      return map;
    }, [foups]);

    const partsByColor = useMemo(() => {
      const map = new Map<string, SubMeshPart[]>();
      for (const colorKey of groups.keys()) {
        map.set(colorKey, getOrRegisterFoupParts(colorKey));
      }
      return map;
    }, [groups, kitRevision]);

    if (foups.length === 0) return null;

    return (
      <group name="InstancedFoupLayer">
        {Array.from(groups.entries()).map(([colorKey, instances]) => {
          const parts = partsByColor.get(colorKey) ?? getOrRegisterFoupParts(colorKey);
          return (
            <InstancedMultiMeshGroup
              key={colorKey}
              layerId={`foup-${colorKey}`}
              parts={parts}
              instances={instances}
              onSelect={onSelect}
              castShadow
              receiveShadow
            />
          );
        })}
      </group>
    );
  }
);

InstancedFoup.displayName = 'InstancedFoup';
export default InstancedFoup;
